import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getContactStatusSuffix } from '../../views/contacts/contactTypeOptions';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import LocationForm from '../../views/contacts/LocationForm';
import PersonForm from '../../views/contacts/PersonForm';
import AssetForm from '../../views/assets/AssetForm';
import JobCreationDetailsPanel from '../jobs/JobCreationDetailsPanel';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useFetchData } from 'services/api/useFetchData';
import { Division } from '../environments/EnvironmentTypes';

// Rename the name of the function:
const ProjectJobForm: React.FC<{
    viewKey?: string;
    linkedItem?: { [key: string]: any };
}> = ({
    viewKey,
    linkedItem,
}) => {
    const { t } = useTranslation();
    const { activeFeatures, userDivision } = useAuthContext();

    // Determine if the job creation details side tab may be shown
    const hasJobCreationDetails = useActiveFeature('job_creation_details');

    // Set the api object
    const apiObject = 'job';

    // Fetch the divisions, to group and restrict the job type options
    const hasDivisionsFeature = activeFeatures.includes('divisions');
    const { response: divisionsResponse } = useFetchData({
        apiUrl: hasDivisionsFeature ? 'get_division_list' : '',
        params: useMemo(() => ({ is_active: true }), []),
    });
    const divisions: Division[] = divisionsResponse?.results ?? [];

    // Configure the job fields
    const fields: FieldType[] = [
        { 
            type: 'searchselect', 
            name: 'client',
            label: 'job.general.client_label',
            placeholder: 'job.general.client_placeholder',
            objectName: 'client.general.object_name.singular',
            query: { endpoint: 'get_contact_search' },
            defaultValue: linkedItem?.client ? {
                id: linkedItem.client.id,
                value: linkedItem.client.name,
                contact_type: linkedItem.client.contact_type,
                blocked: linkedItem.client.blocked,
            } : undefined,
            selectionFormat: 'name',
            selectionSuffix: (option) => getContactStatusSuffix(option),
            optionFormat: {
                title: { field: 'name', format: (value) => value },
                bracket: { field: 'contact_type', format: (value, option) => getContactStatusSuffix(option, value) ?? '', optional: false },
                subtitle: { field: 'primary_location', format: (value) => value.address, optional: true},
            },
            disabled: true
        },
        { 
            type: 'searchselect', 
            name: 'work_location',
            label: 'job.general.work_location_label',
            placeholder: 'job.general.work_location_placeholder',
            objectName: 'location.general.object_name.singular',
            query: { endpoint: 'get_location_search' },
            showOptionsOnClick: () => {
                if (!linkedItem?.client.id) return null;
                return { endpoint: `get_location_search/${linkedItem.client.id}` };
            },
            defaultValue: linkedItem?.work_location ? {
                id: linkedItem.work_location.id,
                value: linkedItem.work_location.address,
            } : undefined,
            selectionFormat: 'address',
            optionFormat: { title: { field: 'address', format: (value) => value }},
            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> },
            directOptionsTitle: 'job.general.locations_of_client_label',
        },
        {
            type: 'multi-searchselect',
            name: 'assets',
            label: 'job.general.assets_label',
            placeholder: 'job.general.assets_placeholder',
            tooltipText: 'job.general.assets_tooltip',
            objectName: 'asset.general.object_name.singular',
            activeFeature: 'asset_management',
            query: (updatedData) => {
                const workLocationId = updatedData?.work_location?.id ?? updatedData?.work_location;
                if (workLocationId) {
                    return { endpoint: `get_asset_search/${workLocationId}` };
                }
                return { endpoint: 'get_asset_search' };
            },
            showOptionsOnClick: (updatedData) => {
                const workLocationId = updatedData?.work_location?.id ?? updatedData?.work_location;
                if (!workLocationId) return null;
                return { endpoint: `get_asset_search/${workLocationId}` };
            },
            directOptionsTitle: 'asset.general.assets_of_location_label',
            selectionFormat: 'name',
            optionFormat: {
                title: { field: 'name', format: (value) => value },
                subtitle: { field: 'number', format: (value) => value, optional: true },
            },
            specialOptions: [
                { value: 'all', label: 'asset.general.all_assets_option', dataField: 'all_assets' },
            ],
            addNewItem: {
                method: 'modal-form',
                modalForm: <AssetForm />,
                linkedItem: (updatedData) => {
                    const workLocationId = updatedData?.work_location?.id ?? updatedData?.work_location;
                    return workLocationId ? { location: workLocationId } : undefined;
                },
            },
        },
        {
            type: 'searchselect',
            name: 'location_person',
            label: 'job.general.contact_at_location_long_label',
            placeholder: 'job.general.contact_at_location_placeholder',
            objectName: 'person.general.object_name.singular',
            query: { endpoint: 'get_person_search' },
            showOptionsOnClick: () => {
                if (!linkedItem?.client.id) return null;
                return { endpoint: `get_person_search/${linkedItem.client.id}` };
            },
            defaultValue: linkedItem?.location_person ? {
                id: linkedItem.location_person.id,
                value: linkedItem.location_person.address,
            } : undefined,
            selectionFormat: 'name',
            optionFormat: { title: { field: 'name', format: (value) => value } },
            addNewItem: { method: 'modal-form', modalForm: <PersonForm /> },
            directOptionsTitle: 'job.general.persons_of_client_label'
        },
        {
            type: 'header',
        },
        {
            type: 'dropdown',
            name: 'division',
            label: 'job.general.division_label',
            apiRequest: [{
                endpoint: 'get_division_list',
                object: 'division',
                params: { is_active: true },
                useCache: true
            }],
            disabled_selected: 'job.general.division_disabled_selected',
            activeFeature: 'divisions',
            defaultValue: userDivision ? { value: String(userDivision) } : undefined,
            allowNoneOption: false,
            showSearch: false,
        },
        {
            type: 'dropdown',
            name: 'workflow',
            label: 'job.general.type_label',
            apiRequest: [{
                endpoint: 'get_workflow_list',
                object: 'workflow',
                params: { is_active: true, connected_feature: 'job' },
                useCache: true
            }],
            disabled_selected: 'job.general.type_disabled_selected',
            activeFeature: 'job_types',
            allowNoneOption: false,
            showSearch: false,
            orderOptions: true,
            groupByField: 'division',
            formatGroupTitle: (group) => divisions.find(division => String(division.id) === group)?.name || group,
            restrictGroupToField: 'division',
        },
        {
            type: 'textarea',
            name: 'main_description',
            label: 'job.general.main_description_label',
            rows: 3,
            placeholder: 'job.general.main_description_placeholder',
        },
        {
            type: 'header',
        },
        {
            type: 'date',
            name: 'target_date',
            label: 'job.general.target_date_label',
            width: '50%',
            group: 'field-row',
        },
        {
            type: 'text',
            name: 'estimated_duration',
            label: 'job.general.estimated_duration_label',
            placeholder: 'job.general.estimated_duration_placeholder',
            width: '50%',
            group: 'field-row',
        },
    ];
    
    return (
        <FormModal
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields}
            linkedItem={linkedItem}
            sidePanel={hasJobCreationDetails ? <JobCreationDetailsPanel /> : undefined}
        />
    );
};

export default ProjectJobForm;